import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { getOrCreateIapUserId } from '../../shared/components/Navigation/iapUserId'
import Footer from '../../shared/components/UIElements/Footer'
import './MasterPlayerHistory.css'

const ORG_NAMES = {
	clubs: 'Clubs',
	coolHockey: 'Cool Hockey Events',
	futureStars: 'Future Stars',
	nsbhl: 'NSBHL',
	teamPitt: 'Team Pitt',
	gtha: 'GTHA',
	murrysville: 'Murrysville',
}

const number = (value) => Number(value) || 0

const totalGoals = (roster) =>
	number(roster.goals) + number(roster.playoffGoals)

const totalAssists = (roster) =>
	number(roster.assists) + number(roster.playoffAssists)

const formatGaa = (value) => number(value).toFixed(2)

const MasterPlayerHistory = () => {
	const navigate = useNavigate()
	const [params] = useSearchParams()

	const firstName = params.get('firstName') || ''
	const lastName = params.get('lastName') || ''

	const [history, setHistory] = useState(null)
	const [loading, setLoading] = useState(true)
	const [photoError, setPhotoError] = useState(false)
	const [error, setError] = useState('')

	useEffect(() => {
		setPhotoError(false)

		if (!firstName || !lastName) {
			setHistory(null)
			setError('No player was selected.')
			setLoading(false)
			return
		}

		const controller = new AbortController()

		const loadHistory = async () => {
			setLoading(true)
			setError('')
			setHistory(null)

			try {
				const query = new URLSearchParams({
					firstName,
					lastName,
				})

				const response = await fetch(
					`${process.env.REACT_APP_BACKEND_URL}/player-history/history?${query}`,
					{
						signal: controller.signal,
						headers: {
							'x-user-id': getOrCreateIapUserId(),
						},
					},
				)

				const data = await response.json()

				if (!response.ok) {
					throw new Error(data.message || 'Unable to load player history.')
				}

				setHistory(data)
			} catch (err) {
				if (err.name !== 'AbortError') {
					setError(err.message || 'Something went wrong.')
				}
			} finally {
				if (!controller.signal.aborted) {
					setLoading(false)
				}
			}
		}

		loadHistory()

		return () => controller.abort()
	}, [firstName, lastName])

	return (
		<div className='mph-page'>
			<header className='mph-header'>
				<button
					type='button'
					onClick={() => navigate('/player-history-search')}
					aria-label='Back to player search'
				>
					‹
				</button>

				<h1>PLAYER HISTORY</h1>
			</header>

			<main className='mph-content'>
				{loading && <p className='mph-message'>Loading player history...</p>}

				{error && <p className='mph-message mph-error'>{error}</p>}

				{history && (
					<>
						<section className='mph-profile'>
							<div
								className={`mph-avatar ${
									history.photo?.filename && !photoError
										? 'mph-avatar-portrait'
										: 'mph-avatar-circle'
								}`}
							>
								{history.photo?.filename && !photoError ? (
									<img
										src={`${process.env.REACT_APP_AWS_BUCKET}/${history.photo.filename}`}
										alt={`${history.firstName} ${history.lastName}`}
										className='mph-avatar-image'
										onError={() => setPhotoError(true)}
									/>
								) : (
									<span className='mph-avatar-initials'>
										{history.firstName?.charAt(0)}
										{history.lastName?.charAt(0)}
									</span>
								)}
							</div>

							<h2>
								{history.firstName} {history.lastName}
							</h2>

							<p>
								{history.organizations
									.map((org) => ORG_NAMES[org.organization] || org.organization)
									.join(' • ')}
							</p>
						</section>

						{history.organizations.map((org) => {
							// Every roster can contain offensive stats,
							// even when isGoalie is true.
							const rosters = (org.runnerRosters || org.rosters || []).filter(
								(roster) =>
									roster.isGoalie !== true ||
									totalGoals(roster) > 0 ||
									totalAssists(roster) > 0,
							)

							// Goalie-specific roster records, with
							// calculated goalieStats from the backend.
							const goalieRosters = org.goalieRosters || []

							const totals = rosters.reduce(
								(acc, roster) => ({
									goals: acc.goals + totalGoals(roster),
									assists: acc.assists + totalAssists(roster),
									pim: acc.pim + number(roster.pim),
								}),
								{
									goals: 0,
									assists: 0,
									pim: 0,
								},
							)

							const goalieTotals = goalieRosters.reduce(
								(acc, roster) => {
									const stats = roster.goalieStats

									if (!stats) return acc

									return {
										wins: acc.wins + number(stats.wins),
										losses: acc.losses + number(stats.losses),
										shutouts: acc.shutouts + number(stats.shutouts),
										gamesPlayed: acc.gamesPlayed + number(stats.gamesPlayed),
										goalsAgainst: acc.goalsAgainst + number(stats.goalsAgainst),
									}
								},
								{
									wins: 0,
									losses: 0,
									shutouts: 0,
									gamesPlayed: 0,
									goalsAgainst: 0,
								},
							)

							const careerGaa =
								goalieTotals.gamesPlayed > 0
									? goalieTotals.goalsAgainst / goalieTotals.gamesPlayed
									: 0

							const hasGoalieHistory = goalieRosters.length > 0

							return (
								<section className='mph-organization' key={org.organization}>
									{rosters.length > 0 && (
										<h3>{ORG_NAMES[org.organization] || org.organization}</h3>
									)}

									{rosters.length === 0 && goalieRosters.length === 0 ? (
										<p className='mph-empty'>
											No playing history recorded for this organization.
										</p>
									) : (
										<>
											{rosters.length > 0 && (
												<>
													{/* OFFENSIVE TOTALS */}
													<div className='mph-totals'>
														<div>
															<strong>{totals.goals}</strong>
															<span>Goals</span>
														</div>

														<div>
															<strong>{totals.assists}</strong>
															<span>Assists</span>
														</div>

														<div>
															<strong>{totals.goals + totals.assists}</strong>
															<span>Points</span>
														</div>

														<div>
															<strong>{totals.pim}</strong>
															<span>PIM</span>
														</div>
													</div>

													{/* OFFENSIVE HISTORY TABLE */}
													<div className='mph-table-scroll'>
														<table className='mph-table'>
															<thead>
																<tr>
																	<th>Season</th>
																	<th>Team</th>
																	<th>G</th>
																	<th>A</th>
																	<th>PTS</th>
																	<th>PIM</th>
																</tr>
															</thead>

															<tbody>
																{rosters.map((roster, index) => (
																	<tr key={String(roster._id || index)}>
																		<td>
																			{roster.session} {roster.year}
																			{roster.leagueName && (
																				<small>{roster.leagueName}</small>
																			)}
																		</td>

																		<td>{roster.teamName || '—'}</td>

																		<td>{totalGoals(roster)}</td>

																		<td>{totalAssists(roster)}</td>

																		<td>
																			{totalGoals(roster) +
																				totalAssists(roster)}
																		</td>

																		<td>{number(roster.pim)}</td>
																	</tr>
																))}
															</tbody>
														</table>
													</div>
												</>
											)}
											{/* GOALIE HISTORY */}
											{hasGoalieHistory && (
												<div className='mph-goalie-section'>
													<h4 className='mph-section-heading'>
														{(
															ORG_NAMES[org.organization] || org.organization
														).toUpperCase()}{' '}
														GOALIE STATS
													</h4>

													{/* GOALIE CAREER TOTALS */}
													<div className='mph-totals'>
														<div>
															<strong>{goalieTotals.wins}</strong>
															<span>Wins</span>
														</div>

														<div>
															<strong>{goalieTotals.losses}</strong>
															<span>Losses</span>
														</div>

														<div>
															<strong>{goalieTotals.shutouts}</strong>
															<span>Shutouts</span>
														</div>

														<div>
															<strong>{formatGaa(careerGaa)}</strong>
															<span>GAA</span>
														</div>
													</div>

													{/* GOALIE SEASON TABLE */}
													<div className='mph-table-scroll'>
														<table className='mph-table mph-goalie-table'>
															<thead>
																<tr>
																	<th>Season</th>
																	<th>Team</th>
																	<th>W</th>
																	<th>L</th>
																	<th>SO</th>
																	<th>GAA</th>
																	<th>GP</th>
																</tr>
															</thead>

															<tbody>
																{goalieRosters.map((roster, index) => {
																	const stats = roster.goalieStats || {}

																	return (
																		<tr key={String(roster._id || index)}>
																			<td>
																				{roster.session} {roster.year}
																				{roster.leagueName && (
																					<small>{roster.leagueName}</small>
																				)}
																			</td>

																			<td>
																				{roster.teamName ||
																					stats.teamName ||
																					'—'}
																			</td>

																			<td>{number(stats.wins)}</td>

																			<td>{number(stats.losses)}</td>

																			<td>{number(stats.shutouts)}</td>

																			<td>{formatGaa(stats.gaa)}</td>

																			<td>{number(stats.gamesPlayed)}</td>
																		</tr>
																	)
																})}
															</tbody>
														</table>
													</div>
												</div>
											)}
										</>
									)}
								</section>
							)
						})}

						{/* VIDEO HIGHLIGHTS */}
						{history.videos?.length > 0 && (
							<section className='mph-videos'>
								<h3>VIDEO HIGHLIGHTS</h3>

								<div className='mph-video-grid'>
									{history.videos.map((video) => (
										<div className='mph-video' key={video.videoId}>
											<iframe
												src={`https://www.youtube-nocookie.com/embed/${encodeURIComponent(video.videoId)}`}
												title={video.videoTitle || `Highlight ${video.videoId}`}
												allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
												allowFullScreen
												loading='lazy'
											/>

											{video.videoTitle && <p>{video.videoTitle}</p>}
										</div>
									))}
								</div>
							</section>
						)}
					</>
				)}
			</main>
			<div className='mph-footer'>
				<Footer />
			</div>
		</div>
	)
}

export default MasterPlayerHistory
